import { inject, observer } from "mobx-react";
import React, { Component } from "react";
import {
  Button,
  PseudoLink,
  Player,
  BackgroundImage,
  ShareButton,
} from "resource-centre-theme/src/views/shared/components";
import { Icons } from "resource-centre-theme/src/libs/assets";
import { tx } from "resource-centre-theme/src/libs/i18n";
import {
  ISingleWordpressDocument,
  ISingleWordpressVideoItem,
} from "resource-centre-theme/src/models";
import { NavigationStore, UIStore } from "resource-centre-theme/src/stores";
import * as s from "./VideoViewer.module.scss";
import { createMarkup } from "resource-centre-theme/src/libs/utils";
import { P } from "resource-centre-theme/src/views/shared/components";
import { FooterLogo } from "resource-centre-theme/src/views/shared/layouts/Footer//FooterLogo";
import { navigate } from "gatsby";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  videoItem: ISingleWordpressVideoItem;
  navigationStore?: NavigationStore;
  document?: ISingleWordpressDocument;
  uiStore?: UIStore;
}

interface State {
  transcriptExpanded: boolean;
}

@inject("navigationStore", "uiStore")
@observer
export class VideoViewer extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      transcriptExpanded: false,
    };
  }

  renderInformationItem = (item, key) => {
    const { label, value, type } = item;
    return (
      <div className={s.informationItem} key={key}>
        <P small className={s.informationItemLabel}>
          {label}
        </P>
        {type != "keyword" && (
          <P small className={s.informationItemValue}>
            {value}
          </P>
        )}
        {type == "keyword" && (
          <div className={s.informationKeywordItemWrapper}>
            {value.map((keywordItem) => {
              return (
                <P key={keywordItem} small className={s.informationKeywordItem}>
                  {keywordItem}
                </P>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  toggleTranscript = () => {
    const { transcriptExpanded } = this.state;
    this.setState({ transcriptExpanded: !transcriptExpanded });
  };

  renderInformationFields = () => {
    const { document } = this.props;
    const { documentFields } = document || {};
    if (!document || !documentFields) return <></>;
    const { format, publicationYear, rights, contributors = [] } =
      documentFields || {};
    const {
      documentTopics: { topics = [] },
      documentKeywords: { keywords = [] },
      documentPublishers: { publishers = [] },
      documentTargetAudiences: { targetAudiences = [] },
      documentContentTypes: { contentTypes = [] },
      documentAuthors: { authors = [] },
    } = document;

    const infos = [
      { label: tx("filter.publicationYear"), value: publicationYear },
      {
        label: tx("filter.targetAudience"),
        value: targetAudiences && targetAudiences.map((i) => i.name).join(", "),
      },
      {
        label: tx("filter.publisher"),
        value: publishers && publishers.map((i) => i.name).join(", "),
      },
      { label: tx("filter.rights"), value: rights },
      {
        label: tx("filter.authors"),
        value: authors && authors.map((i) => i.name).join(", "),
      },
      {
        label: tx("filter.contributors"),
        value: contributors && contributors.map((i) => i.name).join(", "),
      },
      { label: tx("filter.format"), value: format },
      {
        label: tx("filter.contentType"),
        value: contentTypes && contentTypes.map((i) => i.name).join(", "),
      },
      {
        label: tx("filter.topics"),
        value: topics && topics.map((i) => i.name).join(", "),
      },
      {
        label: tx("filter.keywords"),
        value: keywords && keywords.map((i) => i.name),
        type: "keyword",
      },
    ];

    return infos.map((item, i) => this.renderInformationItem(item, i));
  };

  render() {
    const {
      videoItem,
      navigationStore,
      style,
      className,
      children,
      document,
      uiStore,
      ...rest
    } = this.props;
    const { transcriptExpanded } = this.state;
    const { location } = navigationStore;
    const { href } = location;
    const { isDesktop } = uiStore;
    const { mediaItemUrl } = videoItem;

    const { title, documentFields, documentThumbnail } = document;
    const { image } = documentThumbnail;
    const { abstract, sensitiveContent } = documentFields;

    // let localFile = videoItem.localFile;
    // if (!localFile)
    //   localFile = { name: "filename", publicURL: "", prettySize: "", ext: "" };
    // const { publicURL, prettySize, ext: fileExtension } = localFile;

    let videoViewerOverlayClasses = [s.viewerContainer];

    let transcriptClasses = [s.documentAbstract];
    let showTranscriptOverlay =
      abstract && abstract.length > (isDesktop ? 550 : 350) ? true : false;

    if (transcriptExpanded) transcriptClasses.push(s.expanded);

    if (className) videoViewerOverlayClasses.push(className);
    const videoURL = mediaItemUrl;
      // process.env.NODE_ENV === "development" ? publicURL : mediaItemUrl;

    return (
      <div
        className={videoViewerOverlayClasses.join(" ")}
        style={style}
        {...rest}
      >
        <div className={s.controlsContainer}>
          <PseudoLink
            onClick={() => navigate(-1)}
            className={s.closeOverlayButton}
          >
            <img
              src={Icons.cancel_red_bg_white}
              alt="close-video-overlay"
              className={s.closeOverlayIcon}
            />
          </PseudoLink>
        </div>
        <div className={s.contentContainer}>
          <div className={s.videoContainer}>
            <h2>{title}</h2>
            <Player video={videoURL} poster={image} />
            <div className={s.transcriptContainer}>
              <div className={transcriptClasses.join(" ")}>
                <div className={s.expandTranscriptOverlay}>
                  <div
                    className={s.expandTranscriptLabelContainer}
                    onClick={this.toggleTranscript}
                  >
                    <P medium className={s.toggleTranscript}>
                      {tx("document.viewTranscript")}
                    </P>
                    <img
                      src={Icons.chevronUp_large}
                      alt="close-video-overlay"
                      className={s.toggleTranscriptIcon}
                    />
                  </div>
                </div>
                <div
                  className={s.wysiwyg}
                  dangerouslySetInnerHTML={createMarkup(abstract)}
                />
              </div>
            </div>
          </div>
          <div className={s.documentInformationColumn}>
            <div className={s.downloadContainer}>
              <div className={s.documentImage}>
                <BackgroundImage image={image} />
              </div>
              <P medium className={s.documentTitle}>
                {title}
              </P>
              <P small className={s.fileInformation}>
                {`${prettySize} (${fileExtension})`}
              </P>
              <a href={videoURL} download>
                <Button
                  className={s.downloadDocumentButton}
                  size="medium"
                  color="red"
                >
                  {tx("misc.download")}
                </Button>
              </a>
              <div className={s.shareContainer}>
                <P medium className={s.shareLabel}>
                  {tx("misc.share")}
                </P>
                <ShareButton
                  type="twitter"
                  url={href}
                  className={s.shareIcon}
                />
                <ShareButton
                  type="facebook"
                  url={href}
                  className={s.shareIcon}
                />
                <ShareButton
                  type="linkedin"
                  url={href}
                  className={s.shareIcon}
                />
                <ShareButton type="email" url={href} className={s.shareIcon} />
              </div>
            </div>
            <div className={s.documentInformationContainer}>
              <div className={s.informationItem}>
                {sensitiveContent && (
                  <>
                    <P small className={s.informationItemLabel}>
                      {tx("document.sensitiveContent")}
                    </P>
                    <P small className={s.informationItemValue}>
                      {tx("document.sensitiveContentText")}
                    </P>
                    <img
                      src={Icons.sensitive_content}
                      alt="sensitive-content"
                      className={s.icon}
                    />
                  </>
                )}
              </div>
              {this.renderInformationFields()}
            </div>
            <div className={s.provider}>
              <P className={s.providerText}>
                Service provided by{" "}
                <a className={s.navigationItemTitle} href="#">
                  Save The Children Resource Centre
                </a>
              </P>
              <FooterLogo />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
